import Utils from "@/utilities/Utils";
import { mapData } from "@/utilities/DataMapping";
import { IResource, Resource } from "@/model/Resource";
import { IStandardRegistrationField, StandardRegistrationField } from "@/model/StandardRegistrationField";
import { IExtraRegistrationField, ExtraRegistrationField } from "@/model/ExtraRegistrationField";
import { SiteRegistrationType, WorkPermitFormType } from "@/model/Enums";
import { IPermitLocation, PermitLocation } from "@/model/PermitLocation";
import { IPermitZone, PermitZone } from "@/model/PermitZone";

export interface ISiteLookup {
    id: string;
    clientID: string;
    description: string;
    hasWorkPermitForms: boolean;
    deleted: Date;
}

export interface ISite {
    id: string;
    clientID: string;
    activeBriefingID: string;
    name: string;
    logoResourceID: number;
    homeHtml: string;
    imageResourceID: string;
    registrationEmailSubjectTemplate: string;
    registrationEmailBodyTemplate: string;
    failureEmailSubjectTemplate: string;
    failureEmailBodyTemplate: string;
    standardRegistrationFields: Array<IStandardRegistrationField>;
    extraRegistrationFields: Array<IExtraRegistrationField>;
    registrationType: SiteRegistrationType;
    workPermitFormType: WorkPermitFormType;
    workPermitPrefix: string;
    permitLocations: Array<IPermitLocation>;
    permitZones: Array<IPermitZone>;
    includeQrCodeInResultsEmail: boolean;
    created: Date;
    createdByUserID: string;
    lastUpdated: Date;
    lastUpdatedByUserID: string;
    deleted: Date;
    deletedByUserID: string;

    imageResource: IResource;
}

export class Site implements ISite {

    constructor(data?: ISite) {
        if (data) this.update(data);
    }

    update(data: ISite): Site {
        mapData(data, {
            root: () => this,
            standardRegistrationFields: (data: IStandardRegistrationField) => new StandardRegistrationField(data),
            extraRegistrationFields: (data: IExtraRegistrationField) => new ExtraRegistrationField(data),
            imageResource: (data: IResource) => this.imageResource.update(data),
            logoResource: (data: IResource) => this.logoResource.update(data),
            permitLocations: (data: IPermitLocation) => new PermitLocation(data),
            permitZones: (data: IPermitZone) => new PermitZone(data)
        });
        return this;
    }

    id: string = Utils.emptyGuidValue;
    clientID: string = Utils.emptyGuidValue;
    activeBriefingID: string = Utils.emptyGuidValue;
    name: string = "";
    logoResourceID: number = 0;
    homeHtml: string = "";
    imageResourceID: string = Utils.emptyGuidValue;
    registrationEmailSubjectTemplate: string = "";
    registrationEmailBodyTemplate: string = "";
    failureEmailSubjectTemplate: string = "";
    failureEmailBodyTemplate: string = "";
    standardRegistrationFields: Array<StandardRegistrationField> = [];
    extraRegistrationFields: Array<ExtraRegistrationField> = [];
    registrationType: SiteRegistrationType = SiteRegistrationType.WithEmail; // default to sending email (without user needing to choose)
    workPermitFormType: WorkPermitFormType = WorkPermitFormType.None;
    workPermitPrefix: string = "";
    permitLocations: Array<PermitLocation> = [];
    permitZones: Array<PermitZone> = [];
    includeQrCodeInResultsEmail: boolean = false;
    created: Date = new Date(Utils.emptyDateValue);
    createdByUserID: string = Utils.emptyGuidValue;
    lastUpdated: Date = new Date(Utils.emptyDateValue);
    lastUpdatedByUserID: string = Utils.emptyGuidValue;
    deleted: Date = new Date(Utils.emptyDateValue);
    deletedByUserID: string = Utils.emptyGuidValue;

    imageResource: Resource = new Resource();
    logoResource: Resource = new Resource();

    get hasWorkPermitForms() {
        return this.workPermitFormType > WorkPermitFormType.None;
    }

    get isNew() {
        return Utils.isEmptyId(this.id);
    }
}
